import type {
  UpdateExerciseMarketplaceConfigArgs,
  UnarchiveExerciseByIdArgs,
  FetchSignedUploadUrlArgs,
  ArchiveExerciseByIdArgs,
  FetchExercisesArgs,
  CreateExerciseArgs,
  FetchExerciseById,
  EditExerciseArgs,
} from './types/exercises.types';
import type {
  ApiSignedUploadUrl,
  UiSignedUploadUrl,
} from './types/files.types';
import type { ApiExercise } from '../schemas/exercises.schema';
import type { BaseApiResponse } from './types/api.types';

import {
  createExerciseApiArgs,
  mapExerciseFromApi,
} from './mappers/exercises.mappers';
import { postWithPagination, destroy, patch, post, get } from './axios';
import { DEFAULT_CURRENCY } from '../config/constants';

export const fetchSignedUploadUrl = async ({
  mediaType,
  organizationId,
  target,
}: FetchSignedUploadUrlArgs) => {
  const response = await get<BaseApiResponse<ApiSignedUploadUrl>>(
    `/v2${organizationId ? `/organizations/${organizationId}` : ''}/exercises/signed-upload-url?mediaType=${mediaType}${target ? `&target=${target}` : ''}`,
  );
  return response?.data as UiSignedUploadUrl;
};

export const createExercise = async ({
  organizationId,
  ...args
}: CreateExerciseArgs) => {
  const response = await post<ApiExercise>(
    `/v2${organizationId ? `/organizations/${organizationId}` : ''}/exercises`,
    createExerciseApiArgs(args),
  );
  if (!response?.data) return null;
  return mapExerciseFromApi(response?.data, args.locale);
};

export const editExerciseById = async ({
  id,
  organizationId,
  ...args
}: EditExerciseArgs) => {
  const response = await patch<ApiExercise>(
    `/v2${organizationId ? `/organizations/${organizationId}` : ''}/exercises/${id}`,
    createExerciseApiArgs(args),
  );
  if (!response?.data) return null;
  return mapExerciseFromApi(response?.data, args.locale);
};

export const fetchExerciseById = async ({
  id,
  isPart,
  locale,
  organizationId,
  skipView,
}: FetchExerciseById) => {
  let url = `/v2${organizationId ? `/organizations/${organizationId}` : ''}/exercises/${id}${skipView ? '?skipView=true' : ''}`;
  if (isPart) {
    url = `/v2${organizationId ? `/organizations/${organizationId}` : ''}/workout-exercise/${id}`;
  }

  const response = await get<BaseApiResponse<ApiExercise>>(url);
  if (!response?.data) return null;
  return mapExerciseFromApi(response?.data, locale);
};

export const fetchExercises = async ({
  contentFilters,
  locale,
  organizationId,
  tableFilters,
}: FetchExercisesArgs) => {
  const { search, ...restTableFilters } = tableFilters;
  const {
    authorIds,
    isDeleted,
    isHome,
    isMarketplace,
    organizationIds,
    selectedUserIds,
    sources,
    tags,
  } = contentFilters || {};
  const response = await postWithPagination<ApiExercise[]>(
    `/v2${organizationId ? `/organizations/${organizationId}` : ''}/exercises/list`,
    {
      ...restTableFilters,
      ...(search ? { search } : {}),
      ...(tags ? { tagIds: tags } : {}),
      authorIds,
      isDeleted,
      isHome,
      isMarketplace,
      organizationIds,
      selectedUserIds,
      sources,
    },
  );
  if (!response?.data) return null;
  return {
    ...response,
    data: response?.data.map((exercise) =>
      mapExerciseFromApi(exercise, locale),
    ),
  };
};

export const updateExerciseMarketplaceConfig = async ({
  id,
  currency = DEFAULT_CURRENCY,
  enabled,
  organizationId,
  price,
}: UpdateExerciseMarketplaceConfigArgs) => {
  await patch(
    `/v2${organizationId ? `/organizations/${organizationId}` : ''}/exercises/${id}/marketplace-config`,
    {
      configuration: {
        marketplace: { currency, enabled, price },
      },
    },
  );
};

export const archiveExerciseById = async ({
  id,
  organizationId,
}: ArchiveExerciseByIdArgs) => {
  await destroy(
    `/v2${organizationId ? `/organizations/${organizationId}` : ''}/exercises/${id}`,
  );
  return true;
};

export const unarchiveExerciseById = async ({
  id,
  organizationId,
}: UnarchiveExerciseByIdArgs) => {
  await patch(
    `/v2${organizationId ? `/organizations/${organizationId}` : ''}/exercises/${id}/restore`,
  );
  return true;
};
